import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import {
  BASE_URL,
  REPORTS_BASE_URL,
  LIST_ALL_ORDER_TYPE,
  ORDER_TYPE_NEW_FACT_API,
} from "../../../Constants/Config";
import { useAuthDetails } from "../../../Common/cookiesHelper";
import { toThunkRejectValue } from "../../../Common/isRequestCanceled";

const initialState = {
  loading: false,
  orderTypeData: [],
  successMessage: "",
  error: "",
  loadingNew: false,
  orderTypeDataNew: [],
  successMessageNew: "",
  errorNew: "",
  // False until a request has actually settled. The screen treats "not loaded
  // yet" as loading, so the first paint shows the skeleton instead of briefly
  // flashing the empty state while the initial dispatch is still being set up.
  loadedNew: false,
  // requestId of the in-flight request. Because this thunk cancels the previous
  // call on every new one, a superseded request settles AFTER its replacement
  // has already started; without this guard its rejected/fulfilled case would
  // clear loadingNew (killing the skeleton) or overwrite fresh data with stale.
  currentRequestIdNew: null,
};
let cancelTokenSource;
let cancelTokenSourceNew;
// Generate pening , fulfilled and rejected action type
export const fetchOrderTypeData = createAsyncThunk(
  "orderType/fetchOrderTypeData.",
  async (data, { rejectWithValue }) => {
    const { userTypeData } = useAuthDetails();
    if (cancelTokenSource) {
      cancelTokenSource.cancel("Previous request canceled");
    }

    // Create a new cancel token
    cancelTokenSource = axios.CancelToken.source();
    try {
      const { token, ...otherUserData } = userTypeData;
      const response = await axios.post(
        BASE_URL + LIST_ALL_ORDER_TYPE,
        { ...data, ...otherUserData },
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
          cancelToken: cancelTokenSource.token,
        }
      );
      if (response.data.success === true) {
        return response.data.data;
      } else if (
        response.data.success === false &&
        response.data.message === "No data found for the specified criteria"
      ) {
        return response.data;
      }
    } catch (error) {
      // throw new Error(error.response.data.message);
      const customError = {
        message: toThunkRejectValue(error).message,
        status: toThunkRejectValue(error).status,
        data: error.response ? error.response.data : null,
      };
      return rejectWithValue(customError);
    }
  }
);
// Generate pening , fulfilled and rejected action type

export const fetchOrderTypeDataNew = createAsyncThunk(
  "orderType/fetchOrderTypeDataNew",
   async(data, { rejectWithValue })=>{
    const { userTypeData } = useAuthDetails();

    if (cancelTokenSourceNew) {
      cancelTokenSourceNew.cancel("Previous request canceled");
    }

    cancelTokenSourceNew = axios.CancelToken.source();

    try {
      const { token, ...otherUserData } = userTypeData;

      // Fact-table endpoint is FastAPI and reads Form() fields, so the body has
      // to be a real multipart FormData. Passing a plain object with a manual
      // "Content-Type: multipart/form-data" header (what this used to do
      // against PHP) sends JSON with no multipart boundary, which FastAPI
      // rejects as missing required fields. Let axios set the header itself.
      const formData = new FormData();
      const append = (key, value) => {
        if (value === undefined || value === null || value === "") return;
        formData.append(key, value);
      };

      append("merchant_id", data?.merchant_id);
      // The real multi-location filter. Falls back to the merchant itself so a
      // single-store login still scopes correctly.
      append("outlet_ids", data?.outlet_ids || data?.stores || data?.merchant_id);
      append("start_date", data?.start_date);
      append("end_date", data?.end_date);
      append("date_filter", data?.date_filter || "custom");
      // 9 = "All" (both channels). The endpoint also accepts the canonical
      // 1/2/3 and the legacy 5/6 encodings.
      append("order_env", data?.order_env ?? 9);

      Object.entries(otherUserData || {}).forEach(([key, value]) =>
        append(key, value)
      );

      const response = await axios.post(
        REPORTS_BASE_URL + ORDER_TYPE_NEW_FACT_API,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          cancelToken: cancelTokenSourceNew.token,
        }
      );
      if (response.data.success === true) {
        return response.data.data;
      } else if (response.data.success === false) {
        return rejectWithValue({
          message: response.data.message,
          status: response.data.status,
          data: response.data.data,
        });
      }
    } catch (error) {
      const customError = {
        message: toThunkRejectValue(error).message,
        status: toThunkRejectValue(error).status,
        data: error.response ? error.response.data : null,
      };
      return rejectWithValue(customError);
    }
  }
);



const OrderTypeSlice = createSlice({
  name: "orderType",
  initialState,
  extraReducers: (builder) => {
    builder.addCase(fetchOrderTypeData.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(fetchOrderTypeData.fulfilled, (state, action) => {
      state.loading = false;
      state.orderTypeData = action.payload;
      state.error = "";
    });
    builder.addCase(fetchOrderTypeData.rejected, (state, action) => {
      state.loading = false;
      state.orderTypeData = {};
      state.error = action.error.message;
    });
    builder.addCase(fetchOrderTypeDataNew.pending, (state, action) => {
      state.loadingNew = true;
      state.currentRequestIdNew = action.meta.requestId;
    });
    builder.addCase(fetchOrderTypeDataNew.fulfilled, (state, action) => {
      // Ignore a response that belongs to a superseded request.
      if (action.meta.requestId !== state.currentRequestIdNew) return;
      state.loadingNew = false;
      state.loadedNew = true;
      state.currentRequestIdNew = null;
      state.orderTypeDataNew = action.payload;
      state.errorNew = "";
    });
    builder.addCase(fetchOrderTypeDataNew.rejected, (state, action) => {
      // A cancelled request rejects after its replacement is already pending.
      // Clearing loadingNew here would hide the skeleton while that newer
      // request is still running, so only the current request may settle.
      if (action.meta.requestId !== state.currentRequestIdNew) return;
      state.loadingNew = false;
      state.loadedNew = true;
      state.currentRequestIdNew = null;
      state.orderTypeDataNew = {};
      // rejectWithValue puts the message on payload; action.error is just
      // "Rejected" for those, so prefer the payload when it exists.
      state.errorNew =
        action.payload?.message || action.error?.message || "Request failed";
    });
  },
});

export default OrderTypeSlice.reducer;
